import { useEffect, useRef } from "react";
import { C } from "../../shared/theme";
import { pageColumn } from "../../shared/layout";
import { BottomNav } from "../../shared/BottomNav";
import { TopBar } from "../../shared/TopBar";
import { HELP } from "../../shared/helpContent";
import { KIND } from "./theories";
import { useLearnNav, useOpenGroups, parseDetail, parseFeeling } from "./useLearnNav";
import { LearnHome } from "./LearnHome";
import { TheoryList } from "./TheoryList";
import { TheoryDetail } from "./TheoryDetail";
import { FoundationsPage } from "./FoundationsPage";
import { StuckPage } from "./StuckPage";
import { FeelingPage } from "./FeelingPage";
import { ResearchPage } from "./ResearchPage";

// Learn: the psychology of putting things off and getting started.
// onTalk(message) opens Chat with that message; onTalk() opens it ready to talk.
export function LearnPage({ uid, onNav, onTalk }) {
  const nav = useLearnNav(uid);
  const scrollRef = useRef(null);
  const scrollByDepth = useRef({}); // where you were on each page you've left, by stack depth
  const lastDepth = useRef(nav.depth);
  const [openWhy, toggleWhy] = useOpenGroups(uid, KIND.PROBLEM);
  const [openHow, toggleHow] = useOpenGroups(uid, KIND.SOLUTION);

  const open = (view) => {
    if (scrollRef.current) scrollByDepth.current[nav.depth] = scrollRef.current.scrollTop;
    nav.open(view);
  };

  // A new page starts at the top; Back returns to where you were. Focus moves to
  // the page title so keyboard and screen-reader users keep their place.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const wentBack = nav.depth < lastDepth.current;
    el.scrollTop = wentBack ? (scrollByDepth.current[nav.depth] || 0) : 0;
    lastDepth.current = nav.depth;
    el.querySelector("h1")?.focus({ preventScroll: true });
  }, [nav.view, nav.depth]);

  const detail = parseDetail(nav.view);
  const feeling = parseFeeling(nav.view);
  const toToday = () => onNav("today");
  const screens = {
    home: <LearnHome onOpen={open} onTalk={() => onTalk()} />,
    foundations: <FoundationsPage onOpen={open} />,
    why: <TheoryList kind={KIND.PROBLEM} onOpen={open} openGroups={openWhy} onToggleGroup={toggleWhy} />,
    how: <TheoryList kind={KIND.SOLUTION} onOpen={open} openGroups={openHow} onToggleGroup={toggleHow} onToday={toToday} onTalk={() => onTalk()} />,
    stuck: <StuckPage onOpen={open} />,
    research: <ResearchPage />,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, color: C.text }}>
      <TopBar title="Learn" titleAs="div" help={HELP.learn} />
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto" }}>
        {/* Reading width: long lines are hard to follow on a wide screen. */}
        <div style={pageColumn}>
          {!nav.isHome && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 12px 4px" }}>
              <button onClick={nav.back} aria-label="Back"
                style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 22, border: "none", background: "transparent", color: C.text, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
              </button>
            </div>
          )}
          {detail ? <TheoryDetail key={nav.view} kind={detail.kind} n={detail.n} onOpen={open} onTalk={onTalk} />
            : feeling ? <FeelingPage key={nav.view} feeling={feeling} onOpen={open} onToday={toToday} onTalk={onTalk} />
            : screens[nav.view]}
        </div>
      </div>
      {/* Learn has no tab of its own since 20 Oct 2026: it opens from Home's Motivate tile. */}
      <BottomNav active={null} onNav={onNav} />
    </div>
  );
}
